import { addResponseStatusToSpan } from '../../../utils/httpTracing';
import type { Schema } from 'effect';
import { Effect } from 'effect';

import { createBackendApiError } from '../../../data/error/backendApi/_internal/createBackendApiError';
import type { Paths } from '@vs-shop/schemas/openapi/paths';
import { schemaBodyJsonClean } from '@vs-shop/schemas/transform/schemaBodyJsonClean';
import { BackendApi } from '../../../service/api/BackendApi';
import { ApiModule } from '../../../service/logger/ApiModule';
import { logApiOperation } from '../../../utils/logger/apiLogger';
import * as HttpClientResponse from 'effect/unstable/http/HttpClientResponse';

const ENDPOINT = '/api/v2/shop/addresses/{id}';
const HTTP_METHOD = 'put';

export type UpdateAddressRequestBody = Paths[typeof ENDPOINT][typeof HTTP_METHOD] extends {
  requestBody: { content: infer Content };
}
  ? Content extends Record<string, infer Body>
    ? Body
    : never
  : never;

export type MakeUpdateAddressDeps<AddressShowSchema extends Schema.Top, AddressUpdateInput> = {
  readonly addressShow: AddressShowSchema;
  readonly addressUpdateBody: (data: AddressUpdateInput) => UpdateAddressRequestBody;
};

export const makeUpdateAddress = <AddressShowSchema extends Schema.Top, AddressUpdateInput>(
  deps: MakeUpdateAddressDeps<AddressShowSchema, AddressUpdateInput>
) => {
  const UpdateAddressError = createBackendApiError({
    domain: 'Address',
    endpoint: 'Update',
    path: ENDPOINT,
    identifierField: 'addressId',
  });

  const putAddressFetch = (id: string, data: AddressUpdateInput) =>
    Effect.gen(function* () {
      const apiClient = yield* BackendApi;
      return yield* apiClient
        .path(ENDPOINT)
        .method(HTTP_METHOD)
        .accept('application/json', 'application/json')
        .fetch({ params: { id }, body: deps.addressUpdateBody(data) });
    }).pipe(
      Effect.withSpan(`http.${HTTP_METHOD} ${ENDPOINT}`, {
        kind: 'client',
        attributes: {
          'http.request.method': HTTP_METHOD,
          'address.id': id,
        },
      })
    );

  const updateAddress = (id: string, data: AddressUpdateInput) =>
    putAddressFetch(id, data).pipe(
      Effect.map((response) => response.response),
      Effect.tap(addResponseStatusToSpan),
      Effect.flatMap(
        HttpClientResponse.matchStatus({
          '2xx': (response) => schemaBodyJsonClean(deps.addressShow)(response),
          404: (response) =>
            Effect.fail(new UpdateAddressError.NotFoundError({ addressId: id, response })),
          422: UpdateAddressError.createViolationError(id),
          orElse: (response) =>
            Effect.fail(new UpdateAddressError.ApiRequestError({ addressId: id, response })),
        })
      ),
      Effect.tap(() =>
        logApiOperation(ApiModule.AddressUpdate, 'Debug', `Successfully updated address ${id}`)
      ),
      Effect.withSpan('api.address.update', {
        kind: 'internal',
        attributes: {
          'address.id': id,
        },
      })
    );

  return { UpdateAddressError, updateAddress };
};
